<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import {
    message,
    Form,
    Input,
    Select,
    Button,
    Table,
    Space,
    Modal,
    Tag,
    Tooltip,
    Alert
} from 'ant-design-vue';
import { taipeiDay } from '@/utils/taipeiTime';
import { userList } from '@/api/admin/userList';
import {
    getConsultations,
    getConsultants,
    createConsultation,
    updateConsultation,
    getConsultationVersionEligibility
} from '@/api/consultation/consultationApi';
import { getFinanceVersions } from '@/api/finance-version/versionList';
import ConsultationBookingSlotPicker from '@/components/consultation/ConsultationBookingSlotPicker.vue';
import { usePermission } from '@/composables/usePermission';
import formatDateTime from '@/utils/formatDateTime';

const route = useRoute();
const { canView, canUpdate } = usePermission('Consultations');

const loading = ref(false);
const rows = ref([]);
const total = ref(0);
const page = ref(1);
const pageSize = ref(20);
const consultants = ref([]);
const userOptions = ref([]);
const userSearchLoading = ref(false);
let userSearchTimer = null;

const editOpen = ref(false);
const editLoading = ref(false);
const current = ref(null);
/** 開啟編輯／換諮詢師時遞增，強制重拉可預約時段 */
const editSlotReloadToken = ref(0);

const createOpen = ref(false);
const createLoading = ref(false);
const createForm = ref({
    user_id: undefined,
    version_id: undefined,
    consultant_core_member_id: undefined,
    mode: 'online',
    slot_starts_at: null
});
const versionOptions = ref([]);
const versionOptionsLoading = ref(false);

/** 該版本對應的等級資訊 + 系統推薦符合的諮詢師 core_member_id 清單 */
const versionEligibility = ref({
    version_id: null,
    level_code: null,
    level_name: null,
    eligible_core_member_ids: []
});
const eligibilityLoading = ref(false);

/** 給目前選定版本，判斷諮詢師是否符合系統推薦 */
const isConsultantEligibleForVersion = (coreMemberId) => {
    const ids = versionEligibility.value.eligible_core_member_ids || [];
    return ids.includes(coreMemberId);
};

const filters = ref({
    user_id: undefined,
    from: '',
    to: '',
    status: '',
    mode: '',
    consultant_core_member_id: undefined,
    version_id: undefined
});

/** 從版本頁帶入：載入後自動開啟該筆編輯 */
const pendingOpenConsultationId = ref(null);

const consultantOptions = computed(() =>
    consultants.value.map((c) => ({
        label: c.display_name || c.core_member_id,
        value: c.core_member_id
    }))
);

/** 新增預約用：含「符合 / 不符合」標示的諮詢師選項；版本未選時不加標示 */
const consultantOptionsForCreate = computed(() => {
    const hasVersion = !!versionEligibility.value.version_id;
    return consultants.value.map((c) => {
        const name = c.display_name || c.core_member_id;
        const levelTxt = c.level ? `${c.level} 等級` : '未設等級';
        let label;
        if (hasVersion) {
            const ok = isConsultantEligibleForVersion(c.core_member_id);
            label = `${name}（${levelTxt} ${ok ? '符合' : '不符合'}）`;
        } else {
            label = `${name}（${levelTxt}）`;
        }
        return {
            label,
            value: c.core_member_id,
            eligible: hasVersion ? isConsultantEligibleForVersion(c.core_member_id) : null
        };
    });
});

const consultantNameByMemberId = computed(() =>
    Object.fromEntries(
        consultants.value.map((c) => [
            c.core_member_id,
            c.display_name || c.core_member_id
        ])
    )
);

const consultantLabel = (coreMemberId) =>
    consultantNameByMemberId.value[coreMemberId] || coreMemberId || '—';

const statusOptions = [
    { value: 'pending', label: '配對中' },
    { value: 'confirmed', label: '已預約' },
    { value: 'cancelled', label: '已取消' },
    { value: 'completed', label: '已完成' },
    { value: 'no_show', label: '未到' }
];

const modeOptions = [
    { value: 'online', label: '線上' },
    { value: 'offline', label: '實體' }
];

const statusLabel = (status) =>
    statusOptions.find((o) => o.value === status)?.label || status;

const modeLabel = (mode) =>
    modeOptions.find((o) => o.value === mode)?.label || mode;

const versionLabel = (record) =>
    record?.version_name || (record?.version_id ? `版本 #${record.version_id}` : '—');

const statusTagColor = (status) => {
    const map = {
        confirmed: 'processing',
        pending: 'gold',
        cancelled: 'default',
        completed: 'success',
        no_show: 'warning'
    };
    return map[status] || 'default';
};

const modeTagColor = (mode) => (mode === 'online' ? 'blue' : 'purple');

const WEEKDAY_ZH = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'];

const formatBookingDate = (iso) => {
    if (!iso) return '—';
    const d = taipeiDay(iso);
    if (!d.isValid()) return formatDateTime(iso);
    return `${d.format('YYYY-MM-DD')}（${WEEKDAY_ZH[d.day()]}）`;
};

const formatTimeSlot = (record) => {
    const start = taipeiDay(record.starts_at);
    const end = taipeiDay(record.ends_at);
    if (!start.isValid()) return '—';
    if (!end.isValid()) return start.format('HH:mm');
    return `${start.format('HH:mm')} – ${end.format('HH:mm')}`;
};

const versionEditorTo = (versionId) => ({
    name: 'FinanceVersionView',
    params: { id: String(versionId) },
    query: { readonly: '1' }
});

const columns = [
    { title: '日期', key: 'date', width: 148 },
    { title: '時段', key: 'slot', width: 120 },
    { title: '建立時間', key: 'created_at', width: 148 },
    { title: '預約用戶', key: 'u', width: 120, ellipsis: true },
    { title: '諮詢師', key: 'c', width: 120, ellipsis: true },
    { title: '模式', key: 'm', width: 88 },
    { title: '狀態', key: 'st', width: 96 },
    { title: '資產版本', key: 'v', width: 140, ellipsis: true },
    { title: '操作', key: 'actions', width: 88, fixed: 'right' }
];

const loadConsultants = async () => {
    const res = await getConsultants();
    if (res.status !== 1) throw new Error(res.message || '載入諮詢師失敗');
    consultants.value = res.data || [];
};

const searchUsers = async (keyword) => {
    if (userSearchTimer) clearTimeout(userSearchTimer);

    const run = async () => {
        userSearchLoading.value = true;
        try {
            const res = await userList(20, 1, keyword?.trim() ? { name: keyword.trim() } : {});
            const list = Array.isArray(res?.data) ? res.data : [];
            userOptions.value = list.map((u) => ({
                value: u.id,
                label: u.core_member_id
                    ? `${u.name || '會員'}（${u.core_member_id}）`
                    : u.name || `會員 #${u.id}`
            }));
        } catch (e) {
            message.error(e.message || '搜尋會員失敗');
        } finally {
            userSearchLoading.value = false;
            userSearchTimer = null;
        }
    };

    if (!keyword?.trim()) {
        await run();
        return;
    }

    userSearchTimer = setTimeout(run, 300);
};

const fetchList = async () => {
    loading.value = true;
    try {
        const params = {
            page: page.value,
            page_size: pageSize.value
        };
        if (filters.value.from) params.from = filters.value.from;
        if (filters.value.to) params.to = filters.value.to;
        if (filters.value.status) params.status = filters.value.status;
        if (filters.value.mode) params.mode = filters.value.mode;
        if (filters.value.consultant_core_member_id) {
            params.consultant_core_member_id = filters.value.consultant_core_member_id;
        }
        if (filters.value.user_id) {
            params.user_id = filters.value.user_id;
        }
        if (filters.value.version_id) {
            params.version_id = filters.value.version_id;
        }

        const res = await getConsultations(params);
        if (res.status !== 1) throw new Error(res.message || '載入失敗');
        rows.value = res.data?.data || [];
        total.value = res.data?.total || 0;

        const openId = pendingOpenConsultationId.value;
        if (openId && canUpdate.value) {
            const hit = rows.value.find((r) => Number(r.id) === Number(openId));
            pendingOpenConsultationId.value = null;
            if (hit) openEdit(hit);
        }
    } catch (e) {
        message.error(e.message || '載入失敗');
    } finally {
        loading.value = false;
    }
};

const onSearch = () => {
    page.value = 1;
    fetchList();
};

const onReset = () => {
    filters.value = {
        user_id: undefined,
        from: '',
        to: '',
        status: '',
        mode: '',
        consultant_core_member_id: undefined,
        version_id: undefined
    };
    pendingOpenConsultationId.value = null;
    page.value = 1;
    fetchList();
};

const resetVersionEligibility = () => {
    versionEligibility.value = {
        version_id: null,
        level_code: null,
        level_name: null,
        eligible_core_member_ids: []
    };
};

const loadVersionsForUser = async (userId) => {
    versionOptions.value = [];
    createForm.value.version_id = undefined;
    resetVersionEligibility();
    if (!userId) return;

    versionOptionsLoading.value = true;
    try {
        const res = await getFinanceVersions({
            user_id: userId,
            status: 1,
            page: 1,
            page_size: 50
        });
        const list = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res?.data?.data)
              ? res.data.data
              : [];
        versionOptions.value = list.map((v) => ({
            value: v.id,
            label: v.external_name || v.internal_name || `版本 #${v.id}`
        }));
    } catch (e) {
        message.error(e.message || '載入資產版本失敗');
    } finally {
        versionOptionsLoading.value = false;
    }
};

const onVersionChange = async (versionId) => {
    resetVersionEligibility();
    if (!versionId) return;
    eligibilityLoading.value = true;
    try {
        const res = await getConsultationVersionEligibility({ version_id: versionId });
        if (res.status === 1) {
            versionEligibility.value = {
                version_id: res.data?.version_id ?? versionId,
                level_code: res.data?.level_code ?? null,
                level_name: res.data?.level_name ?? null,
                eligible_core_member_ids: Array.isArray(res.data?.eligible_core_member_ids)
                    ? res.data.eligible_core_member_ids
                    : []
            };
        }
    } catch (e) {
        // 對帳失敗不擋使用者：dropdown 退回不加標示
        console.warn('eligibility fetch failed', e);
    } finally {
        eligibilityLoading.value = false;
    }
};

const openCreate = () => {
    createForm.value = {
        user_id: undefined,
        version_id: undefined,
        consultant_core_member_id: undefined,
        mode: 'online',
        slot_starts_at: null
    };
    versionOptions.value = [];
    resetVersionEligibility();
    createOpen.value = true;
};

const saveCreate = async () => {
    const f = createForm.value;
    if (!f.user_id || !f.version_id || !f.consultant_core_member_id || !f.slot_starts_at) {
        message.warning('請填寫會員、資產版本、諮詢師並選擇排班時段');
        return;
    }

    createLoading.value = true;
    try {
        const res = await createConsultation({
            user_id: f.user_id,
            version_id: f.version_id,
            consultant_core_member_id: f.consultant_core_member_id,
            mode: f.mode,
            slot_starts_at: taipeiDay(f.slot_starts_at).isValid()
                ? taipeiDay(f.slot_starts_at).toISOString()
                : f.slot_starts_at
        });
        if (res.status !== 1) throw new Error(res.message || '建立失敗');
        message.success(res.message || '預約已建立');
        createOpen.value = false;
        await fetchList();
    } catch (e) {
        message.error(e.message || '建立失敗');
    } finally {
        createLoading.value = false;
    }
};

const openEdit = (row) => {
    current.value = {
        ...row,
        slot_starts_at: row.starts_at || null,
        cancel_reason: '',
        // 保留進編輯時的原始狀態（給狀態下拉的 disabled 規則判斷用）
        status_original: row.status,
        // 改期：用來判斷是否仍可保留「目前時段」標記
        original_consultant_core_member_id: row.consultant_core_member_id,
        original_mode: row.mode,
        original_starts_at: row.starts_at || null
    };
    editSlotReloadToken.value += 1;
    editOpen.value = true;
};

const canReschedule = computed(
    () => current.value?.status === 'confirmed'
);

/** 諮詢師未改時，把原時段標成「目前」並允許保留（改模式不影響時段） */
const editPreserveSlotStartsAt = computed(() => {
    if (!current.value || !canReschedule.value) return null;
    const sameConsultant =
        current.value.consultant_core_member_id ===
        current.value.original_consultant_core_member_id;
    if (!sameConsultant) return null;
    return current.value.original_starts_at || null;
});

watch(
    () => [current.value?.consultant_core_member_id, current.value?.mode],
    ([consultant, mode], [prevConsultant, prevMode] = []) => {
        // 僅在編輯 modal 已開啟時處理
        if (!editOpen.value || !canReschedule.value) return;
        if (prevConsultant === undefined && prevMode === undefined) return;
        if (consultant === prevConsultant && mode === prevMode) return;

        // 換諮詢師才清空時段；純改模式保留原時段，方便只改線上／實體
        const leftOriginalConsultant =
            consultant !== current.value.original_consultant_core_member_id;
        if (leftOriginalConsultant && consultant !== prevConsultant) {
            current.value.slot_starts_at = null;
            editSlotReloadToken.value += 1;
        }
    }
);

/** 編輯狀態選單：已結束（cancelled/completed/no_show）的紀錄不可再變更狀態，只能維持原狀 */
const TERMINAL_STATES = ['cancelled', 'completed', 'no_show'];
const editStatusOptions = computed(() => {
    const prev = current.value?.status_original;
    const lockedFromTerminal = TERMINAL_STATES.includes(prev);
    return statusOptions.map((o) => ({
        ...o,
        disabled: lockedFromTerminal
            ? o.value !== prev
            : prev === 'pending' && !['pending', 'cancelled'].includes(o.value)
    }));
});

const save = async () => {
    if (!current.value?.id) return;

    if (canReschedule.value && !current.value.slot_starts_at) {
        message.warning('請選擇排班時段');
        return;
    }

    editLoading.value = true;
    try {
        const payload = {
            status: current.value.status,
            mode: current.value.mode,
            consultant_core_member_id: current.value.consultant_core_member_id
        };

        if (canReschedule.value && current.value.slot_starts_at) {
            payload.slot_starts_at = taipeiDay(current.value.slot_starts_at).toISOString();
        }
        if (current.value.status === 'cancelled' && current.value.cancel_reason) {
            payload.cancel_reason = current.value.cancel_reason;
        }

        const res = await updateConsultation(current.value.id, payload);
        if (res.status !== 1) throw new Error(res.message || '儲存失敗');
        message.success(res.message || '已儲存');
        editOpen.value = false;
        await fetchList();
    } catch (e) {
        message.error(e.message || '儲存失敗');
    } finally {
        editLoading.value = false;
    }
};

const applyRouteQueryFilters = () => {
    const q = route.query;
    if (q.consultant_core_member_id) {
        filters.value.consultant_core_member_id = String(q.consultant_core_member_id);
    }
    if (q.from) filters.value.from = String(q.from);
    if (q.to) filters.value.to = String(q.to);
    if (q.status) filters.value.status = String(q.status);
    if (q.mode) filters.value.mode = String(q.mode);
    if (q.user_id) {
        const uid = Number(q.user_id);
        if (Number.isFinite(uid) && uid > 0) filters.value.user_id = uid;
    }
    if (q.version_id) {
        const vid = Number(q.version_id);
        if (Number.isFinite(vid) && vid > 0) filters.value.version_id = vid;
    }
    if (q.consultation_id) {
        const cid = Number(q.consultation_id);
        if (Number.isFinite(cid) && cid > 0) pendingOpenConsultationId.value = cid;
    }
};

onMounted(async () => {
    try {
        await Promise.all([loadConsultants(), searchUsers('')]);
    } catch (e) {
        message.error(e.message || '載入篩選資料失敗');
    }
    applyRouteQueryFilters();
    await fetchList();
});
</script>

<template>
    <div class="space-y-5">
        <p v-if="!canView" class="text-slate-500">無檢視權限</p>
        <template v-else>
            <a-card
                :bordered="false"
                class="admin-panel admin-filter-panel admin-filter-panel--compact"
            >
                <Alert
                    v-if="filters.version_id"
                    type="info"
                    show-icon
                    class="!mb-4"
                    :message="`已依資產版本 #${filters.version_id} 篩選預約`"
                    closable
                    @close="
                        filters.version_id = undefined;
                        onSearch();
                    "
                />
                <Form layout="vertical" class="admin-filter-form">
                    <div class="admin-filter-grid consultations-filter-grid">
                        <Form.Item label="預約會員">
                            <Select
                                v-model:value="filters.user_id"
                                allow-clear
                                show-search
                                :filter-option="false"
                                :loading="userSearchLoading"
                                placeholder="輸入姓名搜尋"
                                class="w-full min-w-0"
                                :options="userOptions"
                                @search="searchUsers"
                            />
                        </Form.Item>
                        <Form.Item label="開始日期">
                            <Input
                                v-model:value="filters.from"
                                type="date"
                                class="w-full min-w-0"
                            />
                        </Form.Item>
                        <Form.Item label="結束日期">
                            <Input
                                v-model:value="filters.to"
                                type="date"
                                class="w-full min-w-0"
                            />
                        </Form.Item>
                        <Form.Item label="狀態">
                            <Select
                                v-model:value="filters.status"
                                allow-clear
                                placeholder="全部"
                                class="w-full min-w-0"
                                :options="statusOptions"
                            />
                        </Form.Item>
                        <Form.Item label="模式">
                            <Select
                                v-model:value="filters.mode"
                                allow-clear
                                placeholder="全部"
                                class="w-full min-w-0"
                                :options="modeOptions"
                            />
                        </Form.Item>
                        <Form.Item label="諮詢師">
                            <Select
                                v-model:value="filters.consultant_core_member_id"
                                allow-clear
                                show-search
                                placeholder="全部"
                                class="w-full min-w-0"
                                :options="consultantOptions"
                                :filter-option="
                                    (input, option) =>
                                        (option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                "
                            />
                        </Form.Item>
                        <div class="admin-filter-actions">
                            <Button type="primary" @click="onSearch">查詢</Button>
                            <Button @click="onReset">重置</Button>
                        </div>
                    </div>
                </Form>
            </a-card>

            <a-card :bordered="false" class="admin-panel admin-data-table">
                <div class="mb-4 flex justify-end">
                    <Button type="primary" :disabled="!canUpdate" @click="openCreate">
                        新增預約
                    </Button>
                </div>
                <Table
                    :columns="columns"
                    :data-source="rows"
                    :loading="loading"
                    row-key="id"
                    :pagination="false"
                    :scroll="{ x: 1080 }"
                    class="custom-table admin-table-surface"
                    :locale="{ emptyText: '尚無資料' }"
                >
                    <template #bodyCell="{ column, record }">
                        <template v-if="column.key === 'date'">
                            {{ formatBookingDate(record.starts_at) }}
                        </template>
                        <template v-else-if="column.key === 'slot'">
                            {{ formatTimeSlot(record) }}
                        </template>
                        <template v-else-if="column.key === 'created_at'">
                            {{ record.created_at ? formatDateTime(record.created_at) : '—' }}
                        </template>
                        <template v-else-if="column.key === 'u'">
                            {{ record.user?.name || '—' }}
                        </template>
                        <template v-else-if="column.key === 'c'">
                            {{ consultantLabel(record.consultant_core_member_id) }}
                        </template>
                        <template v-else-if="column.key === 'm'">
                            <Tag :color="modeTagColor(record.mode)" class="m-0">
                                {{ modeLabel(record.mode) }}
                            </Tag>
                        </template>
                        <template v-else-if="column.key === 'st'">
                            <div class="consultations-status-cell">
                                <Tag :color="statusTagColor(record.status)" class="m-0">
                                    {{ statusLabel(record.status) }}
                                </Tag>
                                <Tooltip
                                    v-if="record.is_orphan"
                                    title="預約系統查無此筆對應紀錄"
                                >
                                    <Tag color="orange" class="m-0 consultations-status-missing">
                                        查無對應
                                    </Tag>
                                </Tooltip>
                            </div>
                        </template>
                        <template v-else-if="column.key === 'v'">
                            <RouterLink
                                v-if="record.version_id"
                                :to="versionEditorTo(record.version_id)"
                                class="text-emerald-700 hover:text-emerald-900 hover:underline font-medium"
                            >
                                {{ versionLabel(record) }}
                            </RouterLink>
                            <span v-else>—</span>
                        </template>
                        <template v-else-if="column.key === 'actions'">
                            <div class="admin-table-actions">
                                <Button
                                    size="small"
                                    class="admin-table-actions__btn"
                                    :disabled="!canUpdate"
                                    @click="openEdit(record)"
                                >
                                    編輯
                                </Button>
                            </div>
                        </template>
                    </template>
                </Table>

                <Space class="mt-5 flex justify-end">
                    <Button :disabled="page <= 1" @click="page--; fetchList()">上一頁</Button>
                    <span class="text-slate-600 text-sm self-center"
                        >第 {{ page }} 頁，共 {{ total }} 筆</span
                    >
                    <Button
                        :disabled="page * pageSize >= total"
                        @click="page++; fetchList()"
                        >下一頁</Button
                    >
                </Space>
            </a-card>

            <Modal
                v-model:open="editOpen"
                title="編輯預約紀錄"
                wrap-class-name="edit-modal consultation-edit-modal"
                :confirm-loading="editLoading"
                ok-text="儲存"
                cancel-text="取消"
                width="640px"
                @ok="save"
            >
                <Form v-if="current" layout="vertical" class="consultation-edit-form">
                    <div class="consultation-edit-form__summary">
                        <span>{{ current.user?.name || '—' }}</span>
                        <span>{{ formatBookingDate(current.starts_at) }} {{ formatTimeSlot(current) }}</span>
                        <span>{{ current.consultant_core_member_id ? consultantLabel(current.consultant_core_member_id) : '尚未配對諮詢師' }}</span>
                    </div>
                    <Alert
                        v-if="current.is_orphan && canReschedule"
                        type="warning"
                        show-icon
                        banner
                        class="consultation-edit-form__alert"
                        message="預約系統查無此筆；改期會先嘗試重建。"
                    />
                    <div class="consultation-edit-form__grid consultation-edit-form__grid--edit">
                        <Form.Item label="諮詢師" class="consultation-edit-form__item">
                            <Select
                                v-model:value="current.consultant_core_member_id"
                                :options="consultantOptions"
                                show-search
                                :placeholder="current.consultant_core_member_id ? '請選擇諮詢師' : '尚未配對'"
                                class="w-full"
                                :filter-option="
                                    (input, option) =>
                                        (option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                "
                            />
                        </Form.Item>
                        <Form.Item label="模式" class="consultation-edit-form__item">
                            <Select
                                v-model:value="current.mode"
                                :options="modeOptions"
                                class="w-full"
                            />
                        </Form.Item>
                        <Form.Item label="狀態" class="consultation-edit-form__item">
                            <Select
                                v-model:value="current.status"
                                :options="editStatusOptions"
                                class="w-full"
                            />
                        </Form.Item>
                    </div>
                    <Form.Item
                        v-if="canReschedule"
                        label="時段"
                        class="consultation-edit-form__item consultation-edit-form__item--slot"
                    >
                        <ConsultationBookingSlotPicker
                            v-model="current.slot_starts_at"
                            :consultant-core-member-id="current.consultant_core_member_id"
                            :mode="current.mode"
                            :preserve-slot-starts-at="editPreserveSlotStartsAt"
                            :reload-token="editSlotReloadToken"
                        />
                    </Form.Item>
                    <Form.Item
                        v-if="current.status === 'cancelled'"
                        label="取消原因"
                        class="consultation-edit-form__item"
                    >
                        <Input.TextArea
                            v-model:value="current.cancel_reason"
                            :rows="2"
                            placeholder="管理員代為取消"
                        />
                    </Form.Item>
                    <div class="consultation-edit-form__version">
                        <span class="consultation-edit-form__version-label">版本</span>
                        <RouterLink
                            v-if="current.version_id"
                            :to="versionEditorTo(current.version_id)"
                            class="consultation-edit-form__version-link"
                        >
                            {{ versionLabel(current) }}
                        </RouterLink>
                        <span v-else class="text-slate-500">—</span>
                    </div>
                </Form>
            </Modal>

            <Modal
                v-model:open="createOpen"
                title="新增預約（管理者代建立）"
                wrap-class-name="edit-modal consultation-edit-modal"
                :confirm-loading="createLoading"
                ok-text="建立預約"
                cancel-text="取消"
                width="720px"
                @ok="saveCreate"
            >
                <Form layout="vertical" class="consultation-edit-form">
                    <div class="consultation-edit-form__grid">
                        <Form.Item label="預約會員" class="consultation-edit-form__item">
                            <Select
                                v-model:value="createForm.user_id"
                                allow-clear
                                show-search
                                :filter-option="false"
                                :loading="userSearchLoading"
                                placeholder="輸入姓名搜尋會員"
                                class="w-full"
                                :options="userOptions"
                                @search="searchUsers"
                                @change="(uid) => loadVersionsForUser(uid)"
                            />
                        </Form.Item>
                        <Form.Item label="資產版本（已完成）" class="consultation-edit-form__item">
                            <Select
                                v-model:value="createForm.version_id"
                                allow-clear
                                show-search
                                :loading="versionOptionsLoading"
                                :disabled="!createForm.user_id"
                                placeholder="請先選擇會員"
                                class="w-full"
                                :options="versionOptions"
                                @change="onVersionChange"
                            />
                        </Form.Item>
                        <Form.Item label="諮詢師" class="consultation-edit-form__item">
                            <Select
                                v-model:value="createForm.consultant_core_member_id"
                                :options="consultantOptionsForCreate"
                                :loading="eligibilityLoading"
                                show-search
                                placeholder="請選擇諮詢師"
                                class="w-full"
                                :filter-option="
                                    (input, option) =>
                                        (option?.label ?? '')
                                            .toLowerCase()
                                            .includes(input.toLowerCase())
                                "
                            />
                        </Form.Item>
                        <Form.Item label="模式" class="consultation-edit-form__item">
                            <Select
                                v-model:value="createForm.mode"
                                :options="modeOptions"
                                class="w-full"
                            />
                        </Form.Item>
                    </div>
                    <Form.Item
                        label="排班時段"
                        class="consultation-edit-form__item consultation-edit-form__item--full"
                    >
                        <ConsultationBookingSlotPicker
                            v-model="createForm.slot_starts_at"
                            :consultant-core-member-id="createForm.consultant_core_member_id"
                            :mode="createForm.mode"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </template>
    </div>
</template>

<style scoped>
.consultations-filter-grid {
    grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
}

@media (min-width: 1100px) {
    .consultations-filter-grid {
        grid-template-columns: minmax(160px, 1.3fr) repeat(5, minmax(120px, 1fr)) auto;
    }
}

.consultations-status-cell {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
}

.consultations-status-missing {
    cursor: help;
}

.consultation-edit-form__alert {
    margin: 0 0 12px;
}

.consultation-edit-form__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-bottom: 12px;
    color: #344054;
    font-weight: 600;
}

.consultation-edit-form__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 12px;
}

.consultation-edit-form__grid--edit {
    grid-template-columns: minmax(0, 1.5fr) minmax(0, 0.75fr) minmax(0, 0.85fr);
}

.consultation-edit-form__item {
    margin-bottom: 12px;
}

.consultation-edit-form__item--full {
    grid-column: 1 / -1;
}

.consultation-edit-form__item--slot {
    margin-bottom: 10px;
}

.consultation-edit-form__item--slot :deep(.ant-form-item-label) {
    padding-bottom: 2px;
}

.consultation-edit-form__version {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-top: 2px;
    font-size: 13px;
}

.consultation-edit-form__version-label {
    color: #94a3b8;
}

.consultation-edit-form__version-link {
    color: #0f766e;
    font-weight: 500;
}

.consultation-edit-form__version-link:hover {
    text-decoration: underline;
}

.consultation-edit-form__hint {
    margin-top: 6px;
    font-size: 12px;
    color: #6b7280;
}
.consultation-edit-form__hint strong {
    color: #1f2937;
    font-weight: 600;
}

@media (max-width: 640px) {
    .consultation-edit-form__grid,
    .consultation-edit-form__grid--edit {
        grid-template-columns: 1fr;
    }
}
</style>
